import { RepairOrderQuoteItems } from '../../form-state';
import { useRepairOrderQuotesStore } from '../repair-order-quotes-context';
import { NumberInput } from '../shared/number-input';
import styles from './styles.module.scss';
import { RepairOrderQuoteStatusEnum } from 'api-new/generated';
import clsx from 'clsx';
import { InlineSvg } from 'components/svg';
import { useCommonLabourRateQuery } from 'entities/repair-order-quote';
import { formatErrorMessage } from 'features/shared/model';
import { FC, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { formatCurrencySafe } from 'utils/formats';

type Props = {
  mainIndex: number;
};
export const LaboursTable: FC<Props> = ({ mainIndex }) => {
  const { isEditMode, dataState } = useRepairOrderQuotesStore();
  const {
    control,
    register,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext<RepairOrderQuoteItems>();

  const [needGetPrice, setNeedGetPrice] = useState<boolean>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `items.${mainIndex}.labor`,
    keyName: 'LaborItemId'
  });

  const { isFetching: isFetchingLabourRate } = useCommonLabourRateQuery([mainIndex], {
    enabled: needGetPrice ?? false,
    onSettled: (data, error) => {
      let price = 150;

      if (error) {
        const message = formatErrorMessage(error);
        toast.error(message);
        console.error(message);
      } else if (data != null) {
        price = data;
      }

      append({
        laborCode: 'LAB',
        price: price,
        allowedTimeInMinutes: 0,
        description: '',
        discount: undefined,
        totalExclVat: undefined,
        totalInclVat: undefined
      });

      setNeedGetPrice(false);
    }
  });

  const handleChangeValue = (index: number, allowedTimeInMinutes: number, price: number, discount: number) => {
    allowedTimeInMinutes /= 60;

    let total = price * allowedTimeInMinutes;

    if (discount && discount != 0) {
      total = total - (discount / 100) * total;
    }

    setValue(`items.${mainIndex}.labor.${index}.totalExclVat`, Number((total / 1.2).toFixed(2)));
    setValue(`items.${mainIndex}.labor.${index}.totalInclVat`, Number(total.toFixed(2)));
  };

  const getTotalInclVat = (index: number) => {
    const val = watch(`items.${mainIndex}.labor.${index}.totalInclVat`);
    return val !== undefined && val !== null ? Number(val).toFixed(2) : '0.00';
  };

  const getTotalExclVat = (index: number) => {
    const val = watch(`items.${mainIndex}.labor.${index}.totalExclVat`);
    return val !== undefined && val !== null ? Number(val).toFixed(2) : '0.00';
  };

  const laborValues = useWatch({ control, name: `items.${mainIndex}.labor` });

  const partTotal =
    laborValues
      ?.filter((x) => !x.isDeleted)
      .reduce((sum, item) => {
        const value = (item?.price ?? 0) * ((item.allowedTimeInMinutes ?? 0) / 60);
        return sum + (isNaN(value) ? 0 : value);
      }, 0) ?? 0;

  const partIncTotal =
    laborValues
      ?.filter((x) => !x.isDeleted)
      .reduce((sum, item) => {
        const value = item?.totalInclVat ?? 0;
        return sum + (isNaN(value) ? 0 : value);
      }, 0) ?? 0;

  const partDiscount =
    laborValues
      ?.filter((x) => !x.isDeleted)
      .reduce((sum, item) => {
        const discount = item?.discount ?? 0;
        const price = item?.price ?? 0;
        const time = (item?.allowedTimeInMinutes ?? 0) / 60;

        if (!discount || !price) return sum;

        return sum + price * time * (discount / 100);
      }, 0) ?? 0;

  const isDeleted = (rowIndex: number) => getValues(`items.${mainIndex}.labor.${rowIndex}.isDeleted`);

  const handleRemoveRow = (rowIndex: number) => {
    const id = getValues(`items.${mainIndex}.labor.${rowIndex}.id`);

    if (!id) {
      remove(rowIndex);

      return;
    }

    setValue(`items.${mainIndex}.labor.${rowIndex}.isDeleted`, true);
    setValue(`items.${mainIndex}.labor.${rowIndex}.needDelete`, true);
  };

  const hasLabor = Boolean(fields?.length);

  const getCdkIcon = (index: number) => {
    if (!Array.isArray(dataState) || mainIndex < 0 || mainIndex >= dataState.length) return null;

    const labours = dataState[mainIndex]?.labours;

    if (!Array.isArray(labours) || index < 0 || index >= labours.length) return null;

    const isSubmittedToCDK = labours[index]?.isSubmittedToCDK;

    if (isSubmittedToCDK == null) {
      return null;
    }

    const path = isSubmittedToCDK ? '/media/icons/custom/check-circle-2.svg' : '/media/icons/custom/information-3.svg';

    return <InlineSvg path={path} className="svg-icon svg-icon-1" />;
  };

  return (
    <div className={styles.container}>
      <div className={styles.containerHeader}>Labours for this Job</div>
      {hasLabor && (
        <div className="table-responsive mt-6">
          <table className={clsx('table', styles.table)}>
            <thead>
              <tr>
                <th style={{ width: '14%' }}>Labour Code</th>
                <th style={{ width: '40%' }}>Description</th>
                <th>Time in hours</th>
                <th>Price</th>
                <th>Discount, %</th>
                <th>Total</th>
                <th>Total (incl VAT)</th>
                <th>CDK</th>
                {isEditMode && <th></th>}
              </tr>
            </thead>
            <tbody>
              {fields?.map((x, index) => (
                <>
                  <tr key={x.LaborItemId}>
                    <td>
                      <span className={styles.mobileHeader}>Labour Code</span>
                      <input
                        type="text"
                        className={`${styles.input}  form-control form-control-solid min-w-md-90px ${
                          isDeleted(index) || errors?.items?.[mainIndex]?.labor?.[index]?.laborCode
                            ? clsx('border-danger', styles.isDeleted)
                            : ''
                        }`}
                        {...register(`items.${mainIndex}.labor.${index}.laborCode`)}
                        disabled={isDeleted(index) || !isEditMode}
                      />
                    </td>
                    <td>
                      <span className={styles.mobileHeader}>Description</span>
                      <input
                        type="text"
                        className={`${styles.input} form-control form-control-solid ${
                          isDeleted(index) || errors?.items?.[mainIndex]?.labor?.[index]?.description
                            ? clsx('border-danger', styles.isDeleted)
                            : ''
                        }`}
                        {...register(`items.${mainIndex}.labor.${index}.description`)}
                        disabled={isDeleted(index) || !isEditMode}
                      />
                    </td>
                    <td className="min-w-md-80px">
                      <span className={styles.mobileHeader}>Time in hours</span>
                      <NumberInput
                        inputValue={(getValues(`items.${mainIndex}.labor.${index}.allowedTimeInMinutes`) ?? 0) / 60}
                        onBlur={(proc) => {
                          const price = getValues(`items.${mainIndex}.labor.${index}.price`) ?? 0;
                          const discount = getValues(`items.${mainIndex}.labor.${index}.discount`) ?? 0;

                          proc *= 60;
                          proc = Math.round(proc);

                          handleChangeValue(index, proc, price, discount);
                          setValue(`items.${mainIndex}.labor.${index}.allowedTimeInMinutes`, proc);
                          trigger();
                        }}
                        isDisabled={isDeleted(index) || !isEditMode}
                        decimalPlaces={2}
                        isError={
                          isDeleted(index) ||
                          errors?.items?.[mainIndex]?.labor?.[index]?.allowedTimeInMinutes?.message != null
                        }
                        setBackColor={isDeleted(index)}
                      />
                    </td>
                    <td className="min-w-md-80px">
                      <span className={styles.mobileHeader}>Price</span>
                      <NumberInput
                        inputValue={getValues(`items.${mainIndex}.labor.${index}.price`) ?? 0}
                        onBlur={(val) => {
                          const time = getValues(`items.${mainIndex}.labor.${index}.allowedTimeInMinutes`) ?? 0;
                          const price = val ?? 0;
                          const discount = getValues(`items.${mainIndex}.labor.${index}.discount`) ?? 0;

                          handleChangeValue(index, time, price, discount);
                          setValue(`items.${mainIndex}.labor.${index}.price`, val);
                        }}
                        isError={isDeleted(index)}
                        isDisabled={isDeleted(index) || !isEditMode}
                        setBackColor={isDeleted(index)}
                      />
                    </td>
                    <td className="min-w-md-90px">
                      <span className={styles.mobileHeader}>Discount, %</span>
                      <NumberInput
                        inputValue={getValues(`items.${mainIndex}.labor.${index}.discount`) ?? 0}
                        onBlur={(val) => {
                          const time = getValues(`items.${mainIndex}.labor.${index}.allowedTimeInMinutes`) ?? 0;
                          const price = getValues(`items.${mainIndex}.labor.${index}.price`) ?? 0;
                          const discount = val;

                          handleChangeValue(index, time, price, discount);
                          setValue(`items.${mainIndex}.labor.${index}.discount`, val);
                        }}
                        isDisabled={isDeleted(index) || !isEditMode}
                        decimalPlaces={0}
                        maxValue={100}
                        isError={isDeleted(index)}
                        setBackColor={isDeleted(index)}
                      />
                    </td>
                    <td>
                      <span className={styles.mobileHeader}>Total</span>
                      <input
                        type="text"
                        className={clsx(
                          styles.input,
                          isDeleted(index) ? clsx('border-danger', styles.isDeleted) : '',
                          'form-control form-control-solid min-w-md-90px'
                        )}
                        value={getTotalExclVat(index) ?? ''}
                        disabled={true}
                      />
                    </td>
                    <td>
                      <span className={styles.mobileHeader}>Total (incl VAT)</span>
                      <input
                        type="text"
                        className={clsx(
                          styles.input,
                          isDeleted(index) ? clsx('border-danger', styles.isDeleted) : '',
                          'form-control form-control-solid min-w-md-90px'
                        )}
                        value={getTotalInclVat(index) ?? ''}
                        disabled={true}
                      />
                    </td>
                    <td>
                      <span className={styles.mobileHeader} style={{ width: '105px' }}>
                        CDK
                      </span>
                      <div className="mt-md-2">{getCdkIcon(index)}</div>
                    </td>
                    {!isDeleted(index) &&
                      isEditMode &&
                      watch(`items.${mainIndex}`).status === RepairOrderQuoteStatusEnum.Created && (
                        <td className={clsx(styles.removeTd)}>
                          <span>&nbsp;</span>
                          <button
                            type="button"
                            onClick={() =>
                              confirm('Are you sure you want to delete this line?') && handleRemoveRow(index)
                            }
                            className={styles.removeBtn}
                          >
                            <InlineSvg path="/media/icons/custom/remove.svg" className="svg-icon-2" />
                          </button>
                        </td>
                      )}
                  </tr>
                  {errors?.items?.[mainIndex]?.labor?.[index]?.allowedTimeInMinutes?.message && (
                    <tr key={x.LaborItemId + '_error'}>
                      <td colSpan={9}>
                        <div className="fv-plugins-message-container">
                          <div className="fv-help-block">
                            {errors?.items?.[mainIndex]?.labor?.[index]?.allowedTimeInMinutes?.message}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>
                  {isEditMode && watch(`items.${mainIndex}`).status === RepairOrderQuoteStatusEnum.Created && (
                    <button
                      type="button"
                      onClick={() => setNeedGetPrice(true)}
                      className="btn btn-light-secondary text-nowrap"
                      style={{ marginBottom: '6px' }}
                    >
                      {needGetPrice || isFetchingLabourRate ? (
                        <span className="indicator-progress" style={{ display: 'block' }}>
                          Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      ) : (
                        <>
                          <InlineSvg path="/media/icons/custom/plus-circle.svg" className="svg-icon-2" />
                          Add labour manually
                        </>
                      )}
                    </button>
                  )}
                </td>
                <td colSpan={7} style={{ verticalAlign: 'top', width: '100%' }}>
                  <div className={clsx(styles.totalRow, 'd-grid gap-6 d-sm-flex', 'text-nowrap')}>
                    <div className="d-flex gap-4">
                      <div className="w-sm-100 text-muted">Total Price</div>
                      <div className="w-sm-100 text-end text-sm-start">{formatCurrencySafe(partTotal)}</div>
                    </div>
                    <div className="d-flex gap-4">
                      <div className="w-sm-100 text-muted">Total Discount</div>
                      <div className="w-sm-100 text-end text-sm-start">{formatCurrencySafe(partDiscount)}</div>
                    </div>
                    <div className="d-flex gap-4">
                      <div className="w-sm-100 text-muted">Total Selling Price (Incl VAT)</div>
                      <div className="w-sm-100 text-end text-sm-start">{formatCurrencySafe(partIncTotal)}</div>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {!hasLabor && isEditMode && watch(`items.${mainIndex}`).status === RepairOrderQuoteStatusEnum.Created && (
        <button
          type="button"
          onClick={() =>
            append({
              laborCode: 'LAB',
              price: 150,
              allowedTimeInMinutes: 0,
              description: '',
              discount: undefined,
              totalExclVat: undefined,
              totalInclVat: undefined
            })
          }
          className="btn btn-light-secondary text-nowrap mt-2"
          style={{ marginBottom: '6px' }}
        >
          <InlineSvg path="/media/icons/custom/plus-circle.svg" className="svg-icon-2" />
          Add labour manually
        </button>
      )}
    </div>
  );
};
